/*
 * Styles the Rubik Combined Listings colour picker to match the theme's own
 * variant picker (.variant-wrapper.color — see assets/custom.css).
 *
 * Why this file exists instead of a rule in custom.css: the app renders into
 * an open shadow root, and it declares its ~76 --rcl-* custom properties twice
 * — once on :host and again on the inner .rcl element via
 * :where([data-rcl-group="N"]). Custom properties inherit from the *nearest*
 * ancestor that declares them, so a rule in custom.css setting them on the
 * host is shadowed by that inner declaration no matter its specificity or
 * !important. The only way in from the theme side is to hand a stylesheet to
 * the shadow root itself, which is what this does.
 *
 * The app also has its own Custom CSS field (Rubik admin > group > Product
 * page desktop/mobile) that lands in the same shadow root. This sheet is
 * appended after it, so these rules win on equal specificity.
 */
(function () {
  'use strict';

  var CSS_TEXT = [
    /* The app's own outer box contributes nothing to the layout; the spacing
       under the picker is set on the light-DOM wrapper in custom.css instead,
       so it sits next to the theme's own .product-block margins rather than
       being split across the shadow boundary. Zeroed here because .rcl's
       margins collapse out through the (plain block) host and would otherwise
       add to it. */
    '.rcl{margin:0;padding:0;}',
    /* Label sits left of the picker, pinned to its top edge — theme's
       .variant-wrapper-label-picker (flex, align-items:flex-start, gap 16px). */
    '.rcl__group{display:flex;align-items:flex-start;gap:16px;margin:0;padding-bottom:0;}',
    /* .variant__label: the whole Body tier, so Theme Settings > Typography >
       Body owns this label the same way it owns the theme's own COLOR / SIZE
       words — the --type* custom properties are declared on :root and inherit
       straight through the shadow boundary, so they can be read in here. The
       app's ~76 --rcl-* properties are the ones that can't be reached from a
       light-DOM rule (see the note at the top of this file); these are the
       other direction and come in for free.

       Spelling the values out instead would quietly unpick the control: the
       Body tier's letter-spacing is an em value, so a hardcoded px equivalent
       holds only while the Body size is left at its current setting.

       The colour is Theme Settings > Colors > Heading text, which is what the
       theme's label resolves to. Nudged 2px down on a 1.2 line-height so the
       cap height lands on the buttons' top border, as in custom.css. */
    '.rcl__group-label{display:flex;align-items:center;gap:5px;margin:0;padding-top:2px;',
    'font-family:var(--typeBodyPrimary),var(--typeBodyFallback);',
    'font-size:var(--typeBodySize);font-weight:var(--typeBodyWeight);',
    'letter-spacing:var(--typeBodySpacing);',
    'line-height:1.2;color:var(--colorTextHeadings,#474747);',
    'text-transform:uppercase;white-space:nowrap;}',
    '.rcl__group-label-option-name{font-family:var(--typeBodyPrimary),var(--typeBodyFallback);',
    'font-size:var(--typeBodySize);font-weight:var(--typeBodyWeight);',
    'letter-spacing:var(--typeBodySpacing);color:var(--colorTextHeadings,#474747);',
    'text-transform:uppercase;}',
    /* The row: no border and no fill of its own. Each button draws its own
       border and a 12px gap separates them — the theme stopped drawing one
       bordered box of edge-to-edge segments (see the "PDP color & size buttons
       — separate bordered buttons with a gap" block in custom.css) and this
       has to follow it. Every option comes out as wide as the widest, exactly
       as the theme's fieldset does.

       This row used to be one line that could not break: `inline-grid` with
       `grid-auto-flow: column` put every option in a column of its own, and
       the button's own 76px width floor (below) stops those columns shrinking
       to fit. Three options fit a tablet-width product column; the Ripple Card
       Holder's four did not — the row kept its ~340px and the fourth button
       sat outside the column, past the right edge of an iPad Air.

       So the columns are explicit now and the row wraps into as many as
       actually fit, 2 × 2 on that iPad. Each track is a fixed
       `--vacier-rcl-col` — the widest button's natural one-line width, measured
       in JS below — because CSS has no content-aware wrap: `repeat(auto-fill,
       …)` only counts repetitions for a *fixed* track size, so `max-content`
       cannot be asked for here, and a hardcoded px guess would be wrong in any
       locale whose colour names run longer than English's.

       Fixed rather than `minmax(…, 1fr)` on purpose: the tracks stay exactly
       as wide as the buttons need, so a row that already fits renders
       pixel-identical to what it did before this change (four 97px buttons on
       a 1440px desktop, measured both ways), and a short row — the two-option
       Veer cuffs — isn't stretched across the column. `min(100%, …)` is the
       one guard on it: a label longer than the whole column would otherwise
       hold a track open past the right edge again.

       `flex: 1 1 auto` + `min-width: 0` is what gives the grid a *definite*
       width to count tracks against. As an `inline-grid`/`auto`-width flex
       item its available space is indefinite, and `auto-fill` resolves to a
       single column in that case — which would stack every option vertically
       instead of wrapping it. */
    '.rcl__grid{display:grid;flex:1 1 auto;min-width:0;width:auto;max-width:100%;',
    'grid-auto-flow:row;',
    'grid-template-columns:repeat(auto-fill,min(100%,var(--vacier-rcl-col,76px)));',
    'justify-content:start;gap:12px;margin:0;padding:0;border:0;}',
    /* One button. Mirrors the theme's .variant-input-wrap label: 34px tall at
       the floor, 76px wide at the floor, 9px/8px padding, a 0.5px hairline
       border and a 10px gap between the colour dot and its value text. Those
       four numbers are the desktop/tablet half of that rule in custom.css and
       have to be changed in both places together, or a combined-listing PDP
       draws visibly different buttons from every other one. The media query at
       the end of this sheet is the mobile half, mirroring custom.css's own. */
    '.rcl__swatch.rcl__swatch--button,',
    '.rcl__swatch.rcl__swatch--button.rcl__swatch--button-with-image{',
    'display:flex;align-items:center;justify-content:center;gap:10px;',
    'width:auto;min-width:76px;min-height:34px;padding:9px 8px;',
    'border:0.5px solid #e7e7e0;border-radius:0;background-color:#fff;box-shadow:none;',
    'box-sizing:border-box;text-align:center;',
    /* Body tier again, same as the label above — the theme puts the button
       value text on the same control as the COLOR word beside it. */
    'font-family:var(--typeBodyPrimary),var(--typeBodyFallback);',
    'font-size:var(--typeBodySize);font-weight:var(--typeBodyWeight);',
    'letter-spacing:var(--typeBodySpacing);line-height:1.2;color:#000;}',
    /* The selected button. The theme drops the border and draws a black 0.5px
       inset shadow in its place, plus the faint #fafafa fill that carries the
       state on a 1x screen where a 0.5px shadow can round away. box-sizing
       above keeps both states the same size through the border swap. */
    '.rcl__swatch.rcl__swatch--button.rcl__swatch--active,',
    '.rcl__swatch.rcl__swatch--button.rcl__swatch--active:hover{',
    'border:0;background-color:#fafafa;box-shadow:inset 0 0 0 0.5px #000;}',
    /* The theme picker has no hover state; drop the app's border-colour shift */
    '.rcl__swatch.rcl__swatch--button:hover{border:0.5px solid #e7e7e0;background-color:#fff;}',
    /* The app sets white-space:nowrap here; the theme's labels wrap, which is
       what lets a cramped row stay inside the group rather than overflow. */
    '.rcl__button-label{display:block;text-align:center;',
    'font-family:var(--typeBodyPrimary),var(--typeBodyFallback);',
    'font-size:var(--typeBodySize);font-weight:var(--typeBodyWeight) !important;',
    'letter-spacing:var(--typeBodySpacing);line-height:1.2;',
    'color:#000;text-transform:uppercase;white-space:normal;}',
    /* The colour dot, at the theme's fixed 10px. flex:0 0 auto so the text
       beside it can't squeeze it on a narrow row. */
    '.rcl__button-image{flex:0 0 auto;width:10px;height:10px;border-radius:100%;}',
    /* Phone sizing, following the theme's own max-width:768px block: shorter
       buttons and the same viewport-scaled padding and dot-to-text gap.

       Where it has to differ: the theme's rows only ever carry three values, so
       they can hold a per-button width floor at every phone width. A combined
       listing has as many values as there are products in the group — four on
       the Ripple Card Holder — and that floor put the fourth button past the
       right edge of the column on a 375px screen.

       So the row sizes to its content and is capped at the width it has:
       `width: max-content` with `max-width: 100%`. Four options overflow that
       cap and shrink to fit; two (the Veer cuffs) stay at their own width
       rather than being stretched to half the column each, which is what a
       flat `width: 100%` did to them.

       `minmax(0, 1fr)` rather than `1fr`, plus `min-width: 0` on the row and on
       the buttons, is what lets the shrinking happen at all: a grid track's
       automatic minimum is its content's min-content width, and the button's
       own width floor would hold it open from the inside. The value text wraps
       to a second line instead — the theme's own labels wrap at this width
       too, and the grid's default `align-items: stretch` keeps every button as
       tall as the tallest. */
    '@media only screen and (max-width:768px){',
    /* .rcl__group keeps the 16px label gap it has on desktop — the theme's
       .variant-wrapper-label-picker doesn't tighten at this width either. */
    /* `grid-auto-flow: column` and `grid-template-columns: none` put this row
       back to the single implicit line the desktop rule above no longer uses.
       Phones don't need the wrap: the buttons have no width floor here
       (`min-width: 0` below), so four of them shrink to fit the column rather
       than overflowing it, and the value text wraps to a second line instead.
       The reset is not optional — the `auto-fill` track list above, counted
       against this row's `max-content` width, resolves to one column and would
       stack every option vertically. */
    '.rcl__grid{display:grid;grid-auto-flow:column;grid-template-columns:none;',
    'flex:0 1 auto;width:max-content;max-width:100%;min-width:0;',
    'grid-auto-columns:minmax(0,1fr);gap:12px;}',
    '.rcl__swatch.rcl__swatch--button,',
    '.rcl__swatch.rcl__swatch--button.rcl__swatch--button-with-image{',
    'min-height:34px;min-width:0;',
    'padding:9px clamp(4px,2vw,10px);gap:clamp(6px,1.6vw,10px);}',
    '}'
  ].join('');

  var sheet = null;
  if (typeof CSSStyleSheet === 'function') {
    try {
      sheet = new CSSStyleSheet();
      sheet.replaceSync(CSS_TEXT);
    } catch (e) {
      sheet = null; // constructable stylesheets unsupported; fall back below
    }
  }

  // Marks roots we've already handled so repeated scans stay cheap.
  var seen = typeof WeakSet === 'function' ? new WeakSet() : null;

  function apply(root) {
    if (!root) return false;

    if (sheet && root.adoptedStyleSheets) {
      // Re-append if the app replaced adoptedStyleSheets on a re-render.
      if (root.adoptedStyleSheets.indexOf(sheet) === -1) {
        root.adoptedStyleSheets = root.adoptedStyleSheets.concat(sheet);
      }
      return true;
    }

    // Fallback: a plain <style> inside the shadow root, last in document order.
    if (!root.querySelector('style[data-vacier-rubik]')) {
      var el = document.createElement('style');
      el.setAttribute('data-vacier-rubik', '');
      el.textContent = CSS_TEXT;
      root.appendChild(el);
    }
    return true;
  }

  /* The widest button's natural, one-line width — the fixed track size the
     wrapping grid above needs, and the one number CSS cannot name for itself.
     Measured by laying the row out as a content-width flex line (no columns,
     nothing to shrink against) and reading the widest child, then handed back
     as --vacier-rcl-col.

     Run in the same task as apply(), so the browser paints once with the
     measured value rather than flashing the 76px fallback first.

     Re-measured on resize because the mobile half of this sheet scales the
     button padding and the dot-to-text gap with the viewport — the natural
     width at phone widths is not the one at desktop — and again on
     fonts.ready, since the value is a text measurement and the theme's fonts
     load async. Keyed on option count + viewport width otherwise, so the
     document-wide MutationObserver's repeated scans don't each force a
     reflow. */
  function sizeTracks(root, force) {
    var grids = root.querySelectorAll('.rcl__grid');

    for (var g = 0; g < grids.length; g++) {
      var grid = grids[g];
      var key = grid.children.length + '@' + window.innerWidth;
      if (!force && grid.vacierTrackKey === key) continue;

      var prev = grid.getAttribute('style');
      grid.style.display = 'inline-flex';
      // flex-shrink has to go with the width: the row is a flex item of
      // .rcl__group, so `width: max-content` is only its flex *basis* and the
      // shrink factor it carries in the sheet would pull it straight back to
      // the space the column has — which is the cramped width being measured
      // away from. Without this the measurement comes back as the button's own
      // 76px floor and the row never widens past it.
      grid.style.flex = '0 0 auto';
      grid.style.width = 'max-content';
      grid.style.maxWidth = 'none';
      grid.style.gridTemplateColumns = 'none';

      var widest = 0;
      for (var i = 0; i < grid.children.length; i++) {
        var w = grid.children[i].getBoundingClientRect().width;
        if (w > widest) widest = w;
      }

      if (prev === null) grid.removeAttribute('style');
      else grid.setAttribute('style', prev);

      if (widest > 0) {
        grid.style.setProperty('--vacier-rcl-col', Math.ceil(widest) + 'px');
        grid.vacierTrackKey = key;
      }
    }
  }

  function scan(force) {
    // Scoped to the product page instances; product-card swatches keep the
    // styling they're configured with in the app.
    var hosts = document.querySelectorAll(
      '.rcl-product-page rubik-combined-listings, rubik-combined-listings'
    );
    var pending = 0;

    for (var i = 0; i < hosts.length; i++) {
      var host = hosts[i];
      if (!host.closest('.rcl-product-page')) continue;

      var root = host.shadowRoot;
      if (!root) {
        pending++; // element upgraded but shadow root not attached yet
        continue;
      }

      apply(root);
      sizeTracks(root, force);

      if (seen && !seen.has(root)) {
        seen.add(root);
        // A re-render swaps the shadow content and can drop our sheet. It can
        // also swap the option list, so the track width is measured again.
        if (typeof MutationObserver === 'function') {
          new MutationObserver(function (root_) {
            return function () { apply(root_); sizeTracks(root_, true); };
          }(root)).observe(root, { childList: true, subtree: true });
        }
      }
    }
    return pending === 0 && hosts.length > 0;
  }

  // A resize can change both halves of the track width: the padding and gap
  // the mobile rules scale with the viewport, and how many of those tracks
  // fit. Coalesced to one measurement a frame.
  var resizeFrame = null;

  function onResize() {
    if (resizeFrame) return;
    resizeFrame = requestAnimationFrame(function () {
      resizeFrame = null;
      scan(true);
    });
  }

  function start() {
    window.addEventListener('resize', onResize);

    // The track width is a text measurement, so it has to be taken again once
    // the theme's webfonts have actually replaced the fallback.
    if (document.fonts && document.fonts.ready && document.fonts.ready.then) {
      document.fonts.ready.then(function () { scan(true); });
    }

    if (scan()) return;

    // The app block loads asynchronously, so keep looking for a short while.
    var tries = 0;
    var timer = setInterval(function () {
      if (scan() || ++tries > 100) clearInterval(timer); // ~20s ceiling
    }, 200);

    if (typeof MutationObserver === 'function') {
      new MutationObserver(function () { scan(); }).observe(document.documentElement, {
        childList: true,
        subtree: true
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
